import { useEffect, useRef } from 'react';
import { PARTICLES_ENABLED } from '@/lib/flags';

interface Props {
  /** Число в центре кольца — дни серии. */
  value: number;
  /** Подпись под числом. */
  label?: string;
  /** Сторона квадрата в пикселях CSS. */
  size?: number;
}

/**
 * Живое кольцо из частиц вокруг числа — момент вехи серии.
 *
 * Зачем канвас, а не картинка или библиотека: кольцо должно дышать и совпадать
 * с палитрой бренда, а тащить ради этого пакет незачем — здесь сотня строк.
 *
 * 🔄 ОТКАТ — одна строка: `PARTICLES_ENABLED = false` в `src/lib/flags.ts`.
 * Тогда компонент рисует то же число без анимации, остальной экран не меняется.
 *
 * Бережём телефон, потому что это мини-апп в вебвью:
 * - частицы считаются от размера, а не «побольше покрасивее»;
 * - при `prefers-reduced-motion` анимации нет вовсе (и людям с вестибулярной
 *   чувствительностью, и это же экономит батарею);
 * - когда вкладку свернули, цикл останавливается, а не крутится вхолостую;
 * - плотность пикселей ограничена двойкой: на телефонах с 3x выигрыша не видно,
 *   а работы втрое больше.
 */
export function ParticleRing({ value, label, size = 220 }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !PARTICLES_ENABLED) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    ctx.scale(dpr, dpr);

    const cx = size / 2;
    const cy = size / 2;
    const radius = size * 0.36;
    const count = Math.round(size * 2.6);

    // Частицы раскладываются один раз: угол, отклонение от кольца, скорость
    // и фаза мерцания. Дальше меняется только время — так кольцо «дышит»,
    // а не кипит, и каждый кадр стоит дёшево.
    const parts = Array.from({ length: count }, () => ({
      angle: Math.random() * Math.PI * 2,
      offset: (Math.random() - 0.5) * size * 0.12,
      speed: 0.00006 + Math.random() * 0.00016,
      phase: Math.random() * Math.PI * 2,
      dot: Math.random() < 0.75 ? 1 : 1.8,
    }));

    let raf = 0;
    let running = true;

    function draw(t: number) {
      if (!ctx) return;
      ctx.clearRect(0, 0, size, size);
      for (const p of parts) {
        const a = p.angle + (reduced ? 0 : t * p.speed);
        // Радиус гуляет по двум синусам — от этого форма живая, а не циркулем.
        const wobble = 1 + 0.08 * Math.sin(3 * a + t * 0.0004) + 0.05 * Math.sin(5 * a);
        const r = radius * wobble + p.offset;
        const x = cx + r * Math.cos(a);
        const y = cy + r * Math.sin(a);
        const near = Math.max(0, 1 - Math.abs(p.offset) / (size * 0.07));
        const twinkle = reduced ? 0.8 : 0.55 + 0.45 * Math.sin(t * 0.0012 + p.phase);
        ctx.globalAlpha = Math.min(1, 0.18 + near * twinkle);
        ctx.fillStyle = '#6fcb9a';
        ctx.beginPath();
        ctx.arc(x, y, p.dot, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
      if (running && !reduced) raf = requestAnimationFrame(draw);
    }

    raf = requestAnimationFrame(draw);

    function onVisibility() {
      if (document.visibilityState === 'hidden') {
        running = false;
        cancelAnimationFrame(raf);
      } else if (!running) {
        running = true;
        raf = requestAnimationFrame(draw);
      }
    }
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      running = false;
      cancelAnimationFrame(raf);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [size]);

  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      {PARTICLES_ENABLED && (
        <canvas
          ref={canvasRef}
          style={{ width: size, height: size }}
          className="absolute inset-0"
          aria-hidden="true"
        />
      )}
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div
          className="font-display font-bold text-white leading-none tabular-nums"
          style={{ fontSize: size * 0.3 }}
        >
          {value}
        </div>
        {label && (
          <div className="text-emerald-glow mt-1" style={{ fontSize: size * 0.075 }}>
            {label}
          </div>
        )}
      </div>
    </div>
  );
}
